import React, { useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Блок «Войти как» для карточки родителя/учителя.
 *
 * Генерирует одноразовую ссылку (POST /api/staff/{id}/impersonate-link) и
 * показывает её с кнопкой «Скопировать». Админ открывает ссылку в другом
 * браузере или окне инкогнито и попадает в CRM под этим пользователем —
 * удобно проверять жалобы «у меня в ЛК не так».
 *
 * Ссылка живёт 10 минут и срабатывает один раз. Право — staff.impersonate
 * (супер-админ имеет всегда). Под администраторов бэкенд войти не даст.
 */
export default function ImpersonateLinkBlock({ userId }) {
  const can = useCan();
  const [link, setLink]       = useState(null);
  const [busy, setBusy]       = useState(false);
  const [copied, setCopied]   = useState(false);
  const [error, setError]     = useState(null);

  if (!can('staff.impersonate', 'write')) return null;

  const generate = async () => {
    if (busy) return;
    setBusy(true); setError(null); setCopied(false);
    try {
      const res = await api.post(`/api/staff/${userId}/impersonate-link`, {});
      setLink(window.location.origin + res.path);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось создать ссылку');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Клипборд недоступен (http/старый браузер) — пользователь скопирует
      // из поля руками, оно ниже.
    }
  };

  return (
    <div className="sd-edit__field sd-edit__field--full"
         style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
      <label className="sd-edit__label" style={{ marginBottom: 6 }}>Войти как этот пользователь</label>

      {!link ? (
        <div>
          <button type="button" className="btn btn--sm" onClick={generate} disabled={busy}>
            <Icon name="external" size={12} /> {busy ? 'Создаём…' : 'Создать ссылку для входа'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input className="sd-edit__input mono" readOnly value={link}
                 style={{ fontSize: 11.5, flex: 1 }}
                 onFocus={(e) => e.target.select()} />
          <button type="button" className="btn btn--sm" onClick={copy}>
            {copied ? '✓ Скопировано' : 'Скопировать'}
          </button>
          <button type="button" className="btn btn--sm" onClick={generate} disabled={busy}
                  title="Сгенерировать новую (старая перестанет действовать по сроку)">
            ↻
          </button>
        </div>
      )}

      {error && <div className="sd-edit__error" style={{ marginTop: 6 }}>{error}</div>}

      <div className="sd-edit__hint" style={{ marginTop: 6 }}>
        Откройте ссылку в <b>другом браузере или окне инкогнито</b> — там
        откроется CRM под этим пользователем. Ссылка одноразовая и действует
        10 минут. В этом окне открывать не стоит (админская сессия заменится
        сессией пользователя), и не пересылайте её в мессенджеры — их
        предпросмотр ссылок может «сжечь» одноразовый вход.
      </div>
    </div>
  );
}
